// Staging/dev Data Ops: live Airtable sync, snapshots, time-travel restore.
// Visible only when /api/health reports features.dataOps.enabled.
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useFeatures } from "../../hooks/useFeatures.js";
import { Badge, Button, Card, CardBody, CardHeader, Input, Spinner } from "../../components/ui.js";
import { SnapshotDiffDrawer, type SnapshotListItem } from "./SnapshotDiffDrawer.js";

interface SnapshotManifest {
  id: string;
  createdAt: string;
  lane: string;
  trigger: string;
  actorEmail: string;
  label: string | null;
  rowCounts: Record<string, number>;
  ref: string;
}

interface ProgressDetail {
  phase?: string;
  phaseLabel?: string;
  current?: number;
  total?: number;
  unit?: string;
  percent?: number;
  log?: string[];
}

interface DataOpsRun {
  id: string;
  kind: string;
  status: string;
  actorEmail: string;
  label: string | null;
  snapshotRef: string | null;
  preSnapshotRef: string | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  detail?: ProgressDetail | null;
}

interface CanaryStatus {
  configured: boolean;
  email: string | null;
  recordId: string | null;
  fieldName: string | null;
  portalValue: string | null;
  airtableValue: string | null;
  match: boolean | null;
}

function ConfirmModal({
  title,
  body,
  confirmWord,
  confirmLabel,
  danger,
  onCancel,
  onConfirm,
  busy,
}: {
  title: string;
  body: string;
  confirmWord: string;
  confirmLabel: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  busy?: boolean;
}): JSX.Element {
  const [typed, setTyped] = useState("");
  const ok = typed.trim().toUpperCase() === confirmWord.toUpperCase();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal>
      <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="mt-2 text-sm text-slate-600">{body}</p>
        <p className="mt-3 text-xs text-slate-500">
          Type <span className="font-mono font-semibold">{confirmWord}</span> to confirm.
        </p>
        <Input
          className="mt-2"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          placeholder={confirmWord}
          disabled={busy}
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant={danger ? "danger" : "primary"} disabled={!ok || busy} onClick={onConfirm}>
            {busy ? "Working…" : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function totalRows(counts: Record<string, number>): number {
  return Object.values(counts).reduce((a, b) => a + b, 0);
}

function statusTone(status: string): "slate" | "blue" | "green" | "amber" | "red" {
  if (status === "succeeded") return "green";
  if (status === "failed") return "red";
  if (status === "running") return "blue";
  if (status === "queued") return "amber";
  return "slate";
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m <= 0) return `${r}s`;
  return `${m}m ${r.toString().padStart(2, "0")}s`;
}

function etaLabel(startedAt: string | null | undefined, percent: number | undefined, kind: string): string {
  if (!startedAt) return kind === "sync" ? "~a few minutes" : "…";
  const elapsedSec = (Date.now() - new Date(startedAt).getTime()) / 1000;
  const p = percent ?? 0;
  if (p > 2) {
    const etaSec = (elapsedSec * (100 - p)) / p;
    return `ETA ~${formatDuration(etaSec)}`;
  }
  return kind === "sync" ? "~a few minutes" : "estimating…";
}

function LiveProgressCard({
  run,
  now,
  onClearStuck,
  clearing,
}: {
  run: DataOpsRun;
  now: number;
  onClearStuck: () => void;
  clearing: boolean;
}): JSX.Element {
  const d = run.detail ?? {};
  const percent = Math.min(100, Math.max(0, d.percent ?? 0));
  const elapsedSec = run.startedAt ? (now - new Date(run.startedAt).getTime()) / 1000 : 0;
  const logs = (d.log ?? []).slice(-8);
  const looksStuck = elapsedSec > 180 && (percent < 1 || logs.length === 0);
  return (
    <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="font-medium">
          {run.kind} · {d.phaseLabel || run.status}
        </div>
        <div className="text-xs text-slate-500">
          {typeof d.current === "number" && typeof d.total === "number"
            ? `${d.current}/${d.total} ${d.unit ?? "steps"} · `
            : null}
          elapsed {formatDuration(elapsedSec)} · {etaLabel(run.startedAt, percent, run.kind)}
        </div>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full bg-blue-500 transition-[width] duration-500" style={{ width: `${percent}%` }} />
      </div>
      <div className="text-xs text-slate-500">{percent}%</div>
      {logs.length > 0 ? (
        <pre className="max-h-36 overflow-auto rounded border border-slate-200 bg-white p-2 font-mono text-[11px] leading-relaxed text-slate-700">
          {logs.join("\n")}
        </pre>
      ) : (
        <p className="text-xs text-slate-500">Waiting for log lines…</p>
      )}
      {looksStuck ? (
        <p className="text-xs text-amber-800">
          No progress for a while — often a zombie after deploy. Clear stuck run to unlock Data Ops.
        </p>
      ) : null}
      <Button variant="danger" disabled={clearing} onClick={onClearStuck}>
        {clearing ? "Clearing…" : "Clear stuck run"}
      </Button>
    </div>
  );
}

function CanaryStrip({
  status,
  expectedToken,
  onFlip,
  flipping,
  disabled,
}: {
  status: CanaryStatus | undefined;
  expectedToken: string | null;
  onFlip: () => void;
  flipping: boolean;
  disabled: boolean;
}): JSX.Element {
  let tone: "slate" | "green" | "amber" | "red" = "slate";
  let label = "unknown";
  if (!status?.configured) {
    label = "not configured";
  } else if (expectedToken) {
    if (status.portalValue === expectedToken) {
      tone = "green";
      label = "synced";
    } else if (status.airtableValue === expectedToken) {
      tone = "amber";
      label = "awaiting sync";
    } else {
      tone = "red";
      label = "mismatch";
    }
  } else if (status.match === true) {
    tone = "green";
    label = "in sync";
  } else if (status.portalValue && status.airtableValue && status.portalValue !== status.airtableValue) {
    tone = "amber";
    label = "portal behind Airtable";
  } else {
    label = "idle";
  }

  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium">Sync canary</h3>
          <p className="text-xs text-slate-500">
            Flip → Sync from Legacy → chip should go green. Writes only the reserved Users field.
          </p>
        </div>
        <Badge tone={tone}>{label}</Badge>
      </div>
      <div className="grid gap-1 text-xs text-slate-600 sm:grid-cols-2">
        <div>
          Portal:{" "}
          <span className="font-mono break-all">{status?.portalValue ?? "(empty)"}</span>
        </div>
        <div>
          Airtable:{" "}
          <span className="font-mono break-all">{status?.airtableValue ?? "(empty)"}</span>
        </div>
        {expectedToken ? (
          <div className="sm:col-span-2">
            Expected: <span className="font-mono break-all">{expectedToken}</span>
          </div>
        ) : null}
      </div>
      <Button variant="secondary" disabled={disabled || flipping || !status?.configured} onClick={onFlip}>
        {flipping ? "Flipping…" : "Flip canary"}
      </Button>
    </div>
  );
}

export function DataOpsPanel(): JSX.Element | null {
  const { features } = useFeatures();
  const enabled = features.dataOps?.enabled === true;
  const qc = useQueryClient();
  const [modal, setModal] = useState<
    null | { kind: "sync" } | { kind: "restore"; ref: string } | { kind: "clearStuck" }
  >(null);
  const [diffTarget, setDiffTarget] = useState<SnapshotListItem | null>(null);
  const [label, setLabel] = useState("");
  const [expectedCanary, setExpectedCanary] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const snapshotsQ = useQuery({
    queryKey: qk.adminDataOpsSnapshots,
    queryFn: () => api.get<{ snapshots: SnapshotManifest[] }>("/admin/dataops/snapshots"),
    enabled,
    refetchInterval: 15_000,
  });

  const runsQ = useQuery({
    queryKey: qk.adminDataOpsRuns,
    queryFn: () => api.get<{ runs: DataOpsRun[]; active: DataOpsRun | null }>("/admin/dataops/runs"),
    enabled,
    refetchInterval: (q) => {
      const active = q.state.data?.active;
      return active && (active.status === "queued" || active.status === "running") ? 2000 : 10_000;
    },
  });

  const canaryQ = useQuery({
    queryKey: [...qk.adminDataOpsCanary, expectedCanary ?? ""],
    queryFn: () => {
      const q = expectedCanary ? `?expected=${encodeURIComponent(expectedCanary)}` : "";
      return api.get<CanaryStatus>(`/admin/dataops/canary${q}`);
    },
    enabled,
    refetchInterval: 10_000,
  });

  const active = runsQ.data?.active ?? null;
  const busy = !!active && (active.status === "queued" || active.status === "running");

  useEffect(() => {
    if (!busy) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [busy]);

  useEffect(() => {
    if (active?.status === "succeeded" || active?.status === "failed") {
      void qc.invalidateQueries({ queryKey: qk.adminDataOpsSnapshots });
      void qc.invalidateQueries({ queryKey: qk.adminDataOpsCanary });
    }
  }, [active?.status, active?.id, qc]);

  // When a sync succeeds after a flip, refresh canary until green or timeout via normal poll.
  useEffect(() => {
    const latest = runsQ.data?.runs?.[0];
    if (latest?.kind === "sync" && latest.status === "succeeded" && expectedCanary) {
      void qc.invalidateQueries({ queryKey: qk.adminDataOpsCanary });
    }
  }, [runsQ.data?.runs, expectedCanary, qc]);

  const syncM = useMutation({
    mutationFn: () => api.post<{ runId: string }>("/admin/dataops/sync", {}),
    onSuccess: () => {
      setModal(null);
      void qc.invalidateQueries({ queryKey: qk.adminDataOpsRuns });
    },
  });

  const snapshotM = useMutation({
    mutationFn: () => api.post<{ runId: string }>("/admin/dataops/snapshot", { label: label.trim() || null }),
    onSuccess: () => {
      setLabel("");
      void qc.invalidateQueries({ queryKey: qk.adminDataOpsRuns });
    },
  });

  const restoreM = useMutation({
    mutationFn: (snapshotRef: string) => api.post<{ runId: string }>("/admin/dataops/restore", { snapshotRef }),
    onSuccess: () => {
      setModal(null);
      void qc.invalidateQueries({ queryKey: qk.adminDataOpsRuns });
    },
  });

  const flipM = useMutation({
    mutationFn: () =>
      api.post<{ token: string; airtableValue: string; portalValueBefore: string | null }>(
        "/admin/dataops/canary/flip",
        {},
      ),
    onSuccess: (res) => {
      setExpectedCanary(res.token);
      void qc.invalidateQueries({ queryKey: qk.adminDataOpsCanary });
    },
  });

  const clearStuckM = useMutation({
    mutationFn: () =>
      api.post<{ cleared: Array<{ id: string; kind: string; status: string }>; message?: string }>(
        "/admin/dataops/runs/clear-stuck",
        {},
      ),
    onSuccess: () => {
      setModal(null);
      void qc.invalidateQueries({ queryKey: qk.adminDataOpsRuns });
    },
  });

  if (!enabled) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold">Data Ops</h2>
            <p className="text-xs text-slate-500">
              Live Airtable sync, JSON snapshots, and time-travel restore (dev/staging only).
            </p>
          </div>
          {busy && active ? (
            <Badge tone="blue">
              {active.kind} · {active.status}
              {typeof active.detail?.percent === "number" ? ` · ${active.detail.percent}%` : ""}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <Button variant="danger" disabled={busy} onClick={() => setModal({ kind: "sync" })}>
            Sync from Legacy (Airtable)
          </Button>
          <div className="flex flex-1 flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1">
              <label className="text-xs text-slate-500">Snapshot label (optional)</label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. before pilot session"
                disabled={busy}
              />
            </div>
            <Button
              variant="secondary"
              disabled={busy || snapshotM.isPending}
              onClick={() => snapshotM.mutate()}
            >
              Take Snapshot
            </Button>
          </div>
        </div>

        {(syncM.error || snapshotM.error || restoreM.error || flipM.error || clearStuckM.error) && (
          <p className="text-sm text-red-600">
            {(syncM.error || snapshotM.error || restoreM.error || flipM.error || clearStuckM.error) instanceof
            Error
              ? (syncM.error || snapshotM.error || restoreM.error || flipM.error || clearStuckM.error)!
                  .message
              : "Request failed"}
          </p>
        )}

        {busy && active ? (
          <LiveProgressCard
            run={active}
            now={now}
            clearing={clearStuckM.isPending}
            onClearStuck={() => setModal({ kind: "clearStuck" })}
          />
        ) : null}

        <CanaryStrip
          status={canaryQ.data}
          expectedToken={expectedCanary}
          onFlip={() => flipM.mutate()}
          flipping={flipM.isPending}
          disabled={busy}
        />

        <div>
          <h3 className="mb-2 text-sm font-medium">Snapshots</h3>
          {snapshotsQ.isLoading ? (
            <Spinner label="Loading snapshots…" />
          ) : (snapshotsQ.data?.snapshots.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-500">No snapshots yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {snapshotsQ.data!.snapshots.map((s) => (
                <li key={s.ref} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium">
                      {s.label || s.trigger}{" "}
                      <span className="font-normal text-slate-400">· {new Date(s.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {s.trigger} · {s.actorEmail} · {totalRows(s.rowCounts)} rows ·{" "}
                      <span className="font-mono">{s.ref}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    disabled={busy || (snapshotsQ.data?.snapshots.length ?? 0) < 2}
                    title="Compare against another snapshot"
                    onClick={() => setDiffTarget(s)}
                  >
                    Diff
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => setModal({ kind: "restore", ref: s.ref })}
                  >
                    Restore
                  </Button>
                </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium">Recent runs</h3>
          {runsQ.isLoading ? (
            <Spinner label="Loading runs…" />
          ) : (runsQ.data?.runs.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-500">No runs yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {runsQ.data!.runs.slice(0, 10).map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2">
                  <Badge tone={statusTone(r.status)}>{r.status}</Badge>
                  <span className="font-medium">{r.kind}</span>
                  <span className="text-slate-400">{new Date(r.createdAt).toLocaleString()}</span>
                  {r.error ? <span className="text-red-600">{r.error}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardBody>

      {modal?.kind === "sync" ? (
        <ConfirmModal
          title="Sync from Legacy Airtable?"
          body="This overwrites editorial data on this lane with a live pull from Airtable. A pre-sync snapshot is taken automatically so you can restore afterward."
          confirmWord="SYNC"
          confirmLabel="Sync now"
          danger
          busy={syncM.isPending}
          onCancel={() => setModal(null)}
          onConfirm={() => syncM.mutate()}
        />
      ) : null}

      {modal?.kind === "restore" ? (
        <ConfirmModal
          title="Restore snapshot?"
          body={`Replace the current database with snapshot "${modal.ref}". A pre-restore snapshot is taken first. You will stay logged in.`}
          confirmWord="RESTORE"
          confirmLabel="Restore"
          danger
          busy={restoreM.isPending}
          onCancel={() => setModal(null)}
          onConfirm={() => restoreM.mutate(modal.ref)}
        />
      ) : null}

      {modal?.kind === "clearStuck" ? (
        <ConfirmModal
          title="Clear stuck Data Ops run?"
          body="Marks the active queued/running run as failed so you can start another. Use this after a deploy recycle left a zombie run (0% / no logs). Does not roll back editorial data."
          confirmWord="CLEAR"
          confirmLabel="Clear stuck run"
          danger
          busy={clearStuckM.isPending}
          onCancel={() => setModal(null)}
          onConfirm={() => clearStuckM.mutate()}
        />
      ) : null}

      {diffTarget ? (
        <SnapshotDiffDrawer
          selected={diffTarget}
          snapshots={snapshotsQ.data?.snapshots ?? [diffTarget]}
          onClose={() => setDiffTarget(null)}
        />
      ) : null}
    </Card>
  );
}
